import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';

const TITLE_OPTIONS = [
  'Architect', 'Town Planner', 'Developer', 'Builder', 'Project Manager',
  'Environmental Consultant', 'Engineer', 'Waste Management Consultant', 'Council Officer', 'Other'
];

const STATE_OPTIONS = [
  { code: '', label: 'Select primary state (optional)' },
  { code: 'VIC', label: 'Victoria' },
  { code: 'QLD', label: 'Queensland' },
  { code: 'WA', label: 'Western Australia' },
  { code: 'SA', label: 'South Australia' },
  { code: 'TAS', label: 'Tasmania' },
  { code: 'ACT', label: 'Australian Capital Territory' },
  { code: 'NSW', label: 'New South Wales (Coming Soon)' }
];

const inputClass = "w-full px-4 py-2.5 text-sm bg-slate-50/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all placeholder:text-slate-300";
const labelClass = "block text-xs font-medium text-slate-500 mb-1";

const Register = () => {
  const [formData, setFormData] = useState({
    email: '', password: '', confirmPassword: '', full_name: '',
    company_name: '', title: '', phone: '', abn: '', primary_state: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.password !== formData.confirmPassword) { setError('Passwords do not match'); return; }
    if (formData.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    const result = await register(formData.email, formData.password, formData.full_name, formData.company_name, formData.title, formData.phone || null, formData.abn || null, formData.primary_state || null);
    setLoading(false);
    if (result.success) navigate('/guidelines?welcome=true');
    else setError(result.error);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col items-center justify-center py-10 px-4" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <SEO
        title="Create Account - PlanGen Consulting"
        description="Sign up to create professional Waste Management Plans for Australian property developments."
        canonicalPath="/register"
        noIndex
      />
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Ambient glow */}
      <div className="fixed inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[20%] left-[20%] w-[350px] h-[350px] bg-blue-100/40 rounded-full blur-[120px]" />
        <div className="absolute bottom-[10%] right-[15%] w-[300px] h-[300px] bg-indigo-100/30 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <div className="text-center mb-8">
        <Link to="/login" className="text-2xl font-bold tracking-tight text-[#0A2540]">PlanGen<span className="text-blue-600">Consulting</span></Link>
        <p className="text-sm text-slate-400 mt-1">Professional Waste Management Plans</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-lg bg-white/80 backdrop-blur-xl rounded-2xl shadow-[0_8px_60px_rgb(0,0,0,0.08)] border border-white/60 p-8" data-testid="register-card">
        <h2 className="text-xl font-bold text-[#0A2540] mb-6">Create your account</h2>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-3 py-2 rounded-xl text-xs mb-5">{error}</div>
        )}

        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Personal Details */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Personal Details</p>
            <div>
              <label htmlFor="full_name" className={labelClass}>Full Name *</label>
              <input id="full_name" name="full_name" type="text" required value={formData.full_name} onChange={handleChange} className={inputClass} data-testid="register-fullname" />
            </div>
            <div>
              <label htmlFor="email" className={labelClass}>Email Address *</label>
              <input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleChange} className={inputClass} data-testid="register-email" />
            </div>
            <div>
              <label htmlFor="phone" className={labelClass}>Phone Number <span className="text-slate-300">(optional)</span></label>
              <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="e.g. 0412 345 678" className={inputClass} data-testid="register-phone" />
            </div>
          </div>

          <div className="h-px bg-slate-100" />

          {/* Company Details */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Company Details</p>
            <div>
              <label htmlFor="company_name" className={labelClass}>Company Name *</label>
              <input id="company_name" name="company_name" type="text" required value={formData.company_name} onChange={handleChange} className={inputClass} data-testid="register-company" />
            </div>
            <div>
              <label htmlFor="title" className={labelClass}>Your Role *</label>
              <select id="title" name="title" required value={formData.title} onChange={handleChange} className={inputClass} data-testid="register-role">
                <option value="">Select your role</option>
                {TITLE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="abn" className={labelClass}>ABN <span className="text-slate-300">(optional)</span></label>
                <input id="abn" name="abn" type="text" value={formData.abn} onChange={handleChange} placeholder="e.g. 12 345 678 901" className={inputClass} data-testid="register-abn" />
              </div>
              <div>
                <label htmlFor="primary_state" className={labelClass}>Primary State <span className="text-slate-300">(optional)</span></label>
                <select id="primary_state" name="primary_state" value={formData.primary_state} onChange={handleChange} className={inputClass} data-testid="register-state">
                  {STATE_OPTIONS.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div className="h-px bg-slate-100" />

          {/* Password */}
          <div className="space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-400">Set Password</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="password" className={labelClass}>Password *</label>
                <input id="password" name="password" type="password" autoComplete="new-password" required value={formData.password} onChange={handleChange} className={inputClass} data-testid="register-password" />
              </div>
              <div>
                <label htmlFor="confirmPassword" className={labelClass}>Confirm Password *</label>
                <input id="confirmPassword" name="confirmPassword" type="password" autoComplete="new-password" required value={formData.confirmPassword} onChange={handleChange} className={inputClass} data-testid="register-confirm-password" />
              </div>
            </div>
            <p className="text-[10px] text-slate-400">Minimum 6 characters</p>
          </div>

          <button type="submit" disabled={loading}
            className="w-full py-2.5 rounded-full bg-[#0A2540] text-white text-sm font-semibold hover:bg-black transition-colors disabled:opacity-50 shadow-lg shadow-slate-900/10"
            data-testid="register-submit">
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="text-center text-xs text-slate-400 mt-6">
          Already registered? <Link to="/login" className="text-blue-600 font-medium hover:text-blue-700">Sign in to your account</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
